import { InputText } from 'primereact/inputtext';
import { Control, Controller, ControllerRenderProps, FieldValues, Path, PathValue, RegisterOptions } from 'react-hook-form';
import { TextBodyExtraExtraSmall, TextBodyExtraSmall, TextBodyExtraSmall2, TextBodyLarge, TextLabelExtraSmall } from '@/components/text/Texts';
import IdGeneratorService from '@/services/id-generator.service';
import Container, { Column, Row } from '@/components/container/Container';
import { MdLocationOn, MdHistory, MdStar, MdSearch, MdFavorite, MdAddCircle, MdCorporateFare } from "react-icons/md";
import { useCallback, useEffect, useState } from 'react';
import { placesRepository } from '../views/PlacesRepository';
import { X4bLocation } from '../views/PlacesRepository';
import { debounce } from 'lodash';
import { showError } from '@/modules/notification/engine/notification.library';
import { cn } from '@/components/ui/cn';
import { Button } from 'primereact/button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ENDPOINTS } from '@/constants/endpoints';
import { GenericResponse, ListResponse, showTypedModal } from '@/common/common_utils';
import { axiosInstance } from '@/services/axios.service';
import AddNewPlaceModal from './AddNewPlaceModal';

export enum X4bPointType {
	Pickup = 0,
	Dropoff,
}

interface CompanySavedPoint extends X4bLocation {
	Id: number;
}
interface GetCompanyPointsRequest { PointType: X4bPointType }
interface GetCompanyPointsResponse { Points: CompanySavedPoint[] }

enum AddressesTabType {
	Popular = 1,
	Recent = 2,
	Saved = 3,
}

interface Props<T extends FieldValues> {
	id: Path<T>;
	control: Control<T>;
	formName: string;
	label?: string;
	rules?: RegisterOptions<T, Path<T>>;
	defaultValue?: PathValue<T, Path<T>>;
	pointType: X4bPointType;
}

const AddressDropdownInput = <T extends FieldValues>({
	id,
	control,
	formName,
	label,
	rules,
	defaultValue,
	pointType,
}: Props<T>) => {
	const [searchValue, setSearchValue] = useState('');
	const [popularAddresses, setPopularAddresses] = useState<X4bLocation[]>([]);
	const [recentAddresses, setRecentAddresses] = useState<X4bLocation[]>([]);
	const [tabType, setTabType] = useState(AddressesTabType.Popular);
	const idService = new IdGeneratorService(formName);
	const idGenerated = idService.generateFormInputIds(id);

	const [isFocus, setIsFocus] = useState(false);
	const queryClient = useQueryClient();

	const companyPointsQ = useQuery({
		queryKey: [`AddressDropdownInput-${id}`],
		queryFn: async () => {
			const result = await axiosInstance.post<GenericResponse<GetCompanyPointsResponse>>(ENDPOINTS['/api/X4bActivity/GetCompanyPoints'], {
				PointType: pointType
			} as GetCompanyPointsRequest);

			return result.data;
		},
	});

	const debouncedSearch = useCallback(
		debounce(async (value: string) => {
			if (value.trim().length == 0) setPopularAddresses([])
			else {
				try {
					const result = await placesRepository.searchPlaces(value);
					setPopularAddresses(result);
				} catch (error) {
					showError('Failed to fetch addresses', { summary: 'Error' });
				}
			}
		}, 300), []
	);
	const debouncedSearchRecent = useCallback(
		debounce(async (value: string) => {
			try {
				const result = await axiosInstance.post<GenericResponse<ListResponse<X4bLocation>>>(
					ENDPOINTS['/api/X4bActivity/GetCompanyRecentPoints'],
					JSON.stringify(value),
					{ headers: { 'Content-Type': 'application/json' } }
				);
				setRecentAddresses(result.data.Data?.Items ?? []);
			} catch (error) {
				showError('Failed to fetch recent addresses', { summary: 'Error' });
			}
		}, 300), []
	);

	useEffect(() => {
		debouncedSearch(searchValue);
		return () => {
			debouncedSearch.cancel();
		};
	}, [searchValue, debouncedSearch]);

	useEffect(() => {
		if (tabType === AddressesTabType.Recent) {
			debouncedSearchRecent(searchValue);
		}
		return () => {
			debouncedSearchRecent.cancel();
		};
	}, [searchValue, tabType, debouncedSearchRecent]);

	const addressesList = (field: any, list: X4bLocation[]) => {
		if (list.length === 0) return;
		return (
			<>
				{/* Divider */}
				<div className="h-[1px] mx-4 mt-2 bg-neutralVariant30" />

				{/* Address List */}
				<Column
					className="py-3"
					children={list.map((option, index) => (
						<Row key={index} className="h-16 gap-4 pl-4 pr-6 items-center cursor-pointer duration-300 hover:bg-[#DBDEE0]"
							/* 1. Can't use onClick here, because it gets processed after focus loss which sets display:none.
								2. Instead of state variable we use css to show/hide dropdown because it is much more predictable and easier to debug.
								TODO[Phase2]: I dont think we should control dropdown visibility via input focus because its not flexible enough
												we need to manage both input focus and dropdown visibility ourselves similar to how primereact handles it
							*/
							onClick={() => {
								field.onChange(option);
								// TODO: I think here we need to replicate UI we have for address input in flutter. We need to show both placeName and address. Only this way we can avoid ambiguity. Confirm with managers.
								setSearchValue(option.Address);
								// Hide dropdown by blurring the input
								const inputElement = document.getElementById(idGenerated.id);
								if (inputElement) inputElement.blur();
							}}
						>
							<MdLocationOn className="text-primary shrink-0" size={24} />
							<Column className="w-[calc(100%-(24px+16px))] py-1"> {/* Icon Size + Gap */}
								<TextBodyLarge text={option.PlaceName} className="text-onSurface whitespace-nowrap truncate" />
								<TextBodyExtraSmall2 text={option.Address} className="text-neutralVariant20 whitespace-nowrap truncate" />
							</Column>
						</Row>
					))}
				/>
			</>
		);
	};

	const dropdownBtn = (addressTab: AddressesTabType, text: string, icon: React.ReactNode) => (
		<Button
			icon={icon} label={text}
			onClick={() => setTabType(addressTab)}
			pt={{
				root: {
					className: cn(
						'h-10 pl-4 pr-6 rounded-full gap-2 cursor-pointer',
						'transition-all duration-200 hover:bg-[#C9D3D9FF]',
						tabType === addressTab ? 'bg-[#BDC7CDFF]' : 'bg-primary99'
					)
				},
				label: { className: 'font-poppins text-label-large2 text-secondary60' },
			}}
		/>
	);

	const dropdown = (field: ControllerRenderProps<T, Path<T>>) => (
		<Column
			onMouseDown={e => e.preventDefault()}
			className={cn(
				'absolute z-50 w-full bg-surface rounded-b-[28px]',
				'shadow-[0px_1px_3px_0px_#0000004D,0px_4px_8px_3px_#00000026]',
				'hidden peer-focus:block',
			)}
		>
			<Row className="flex-wrap p-3 gap-3">
				{dropdownBtn(AddressesTabType.Popular, 'Popular', (<MdStar className="text-secondary60" size={18} />))}
				{dropdownBtn(AddressesTabType.Recent, 'Recent', (<MdHistory className="text-secondary60" size={18} />))}
				{dropdownBtn(AddressesTabType.Saved, 'Save Places', (<MdFavorite className="text-secondary60" size={18} />))}
			</Row>

			{tabType === AddressesTabType.Popular && (addressesList(field, popularAddresses))}
			{tabType === AddressesTabType.Recent && (addressesList(field, recentAddresses))}
			{tabType === AddressesTabType.Saved && (
				<>
					<div className="h-[1px] ml-4 mt-2 mb-3 bg-neutralVariant30" />

					{(!companyPointsQ.data || companyPointsQ.data.Data.Points.length == 0) && (
						<Row
							className="h-[72px] mx-3 rounded-[5px] items-center justify-center bg-primary99"
							children={(<TextLabelExtraSmall text="No Saved locations yet" className="text-primary90" />)}
						/>
					)}

					{(companyPointsQ.data && companyPointsQ.data.Data.Points.length > 0) && (
						<Column>
							{companyPointsQ.data.Data.Points.map((savedPoint, i) => {
								return (
									<Row
										key={`savedPoint-${i}`}
										className="h-16 gap-4 pl-4 pr-6 items-center cursor-pointer duration-300 hover:bg-[#DBDEE0]"
										onClick={() => {
											field.onChange({
												Latitude: savedPoint.Latitude,
												Longitude: savedPoint.Longitude,
												PlaceName: savedPoint.PlaceName,
												Address: savedPoint.Address,
												Name: savedPoint.Name
											} as X4bLocation);
											setSearchValue(savedPoint.Address);
											const inputElement = document.getElementById(idGenerated.id);
											if (inputElement) inputElement.blur();
										}}
									>
										<MdCorporateFare size={24} className="text-primary" />
										<Column className="w-[calc(100%-(24px+16px))]"> {/* Icon Size + Gap */}
											<TextBodyLarge text={savedPoint.Name} className="text-onSurface whitespace-nowrap truncate" />
											<TextBodyExtraSmall text={savedPoint.Address} className="text-onSurfaceVariant whitespace-nowrap truncate" />
										</Column>
									</Row>
								);
							})}
						</Column>
					)}

					<div className="h-[1px] ml-4 my-2 bg-neutralVariant30" />
					<Button
						onClick={async () => {
							await showTypedModal(AddNewPlaceModal, { pointType: pointType });
							companyPointsQ.refetch();
						}}
						className="h-12 w-full gap-4 pl-4 pr-6 mb-3 bg-transparent cursor-pointer duration-300 hover:bg-[#DBDEE0]"
					>
						<MdAddCircle size={24} className="text-primary" />
						<TextBodyLarge text="Add new place" className="text-onSurface" />
					</Button>
				</>
			)}

		</Column>
	);

	return (
		<Controller
			control={control}
			name={id}
			rules={rules}
			defaultValue={defaultValue}
			render={({ field, fieldState: { error } }) => {
				// Sync searchValue with field value on mount/change
				useEffect(() => {
					const location = field.value as X4bLocation | undefined;
					if (location?.Address !== searchValue) {
						setSearchValue(location?.Address || '');
					}
				}, [field.value]);

				const hasValue = !!searchValue;

				return (
					<Column>
						<Container className="relative">
							<MdSearch size={24} className={cn(
								'text-neutralVariant20 pointer-events-none',
								'absolute top-1/2 -translate-y-1/2 left-3',
							)} />
							<InputText
								id={idGenerated.id}
								// using to avoid browser autocomplete. off value is not working
								autoComplete="new-password"
								value={searchValue}
								onChange={(e) => {
									setSearchValue(e.target.value)
									if (field.value && e.target.value !== field.value.Address)
										field.onChange(undefined);
								}}
								onFocus={() => setIsFocus(true)}
								onBlur={() => {
									setIsFocus(false);
									field.onBlur();
								}}
								className={cn(
									'w-full h-14 pl-12 pr-4',
									'bg-white bg-none rounded-[4px]',
									'peer transition-all duration-200',
									'font-poppins text-body-large text-neutralVariant20',
									'appearance-none focus:outline-none focus:ring-0',
									'transition-all duration-200 outline-none', isFocus
									? error ? 'shadow-[inset_0_0_0_3px_theme(colors.error60)]' : 'shadow-[inset_0_0_0_3px_theme(colors.primary60)]'
									: error ? 'shadow-[inset_0_0_0_1px_theme(colors.error60)]' : 'shadow-[inset_0_0_0_1px_theme(colors.primary60)]',
								)}
							/>
							<label
								htmlFor={idGenerated.id}
								children={label ?? idService.generateLabel(id)}
								className={cn(
									'absolute z-10 origin-[0] px-1',
									'transition-all duration-300',
									'font-poppins text-body-large',
									error ? 'text-error60' : isFocus ? 'text-primary60' : 'text-neutralVariant20',
									(isFocus || hasValue) ? 'bg-white' : 'bg-transparent',
									(isFocus || hasValue) ? 'left-4' : 'left-11',
									(isFocus || hasValue) ? '-translate-y-1/2 scale-[78%]' : 'translate-y-1/2 scale-100',
								)}
							/>
							{dropdown(field)}
						</Container>

						{(error) && (
							<TextBodyExtraExtraSmall
								id={`${idGenerated.id}-helper`}
								className="block pl-[16px] mt-1 text-error"
								text={error?.message ?? ''}
							/>
						)}
					</Column>
				);
			}}
		/>
	);
};





export default AddressDropdownInput;
